import ISupplierModel from "@models/ISupplierModel";
import IUserUI from "@models/IUserUI";
import ReportTypeEnum from "@models/ReportTypeEnum";
import UserTypesEnum from "@models/UserTypesEnum";
import { FilterValues, IFilterField, IFilterSection, IFilterSelectOption } from "@models/IFilterConfig";

export interface IReportsFilterContext {
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    /** The supplier has custom reports, so `report_type=custom` is offered */
    hasCustomReports: boolean;
    /** Translate a message id (used for the report type labels) */
    formatMessage: (id: string) => string;
}

/** Baseline Reports filters: the legacy reset sends an empty query. */
export const REPORTS_DEFAULT_FILTERS: FilterValues = {};

const MANAGEMENT_USER_TYPES: number[] = [UserTypesEnum.ADMIN, UserTypesEnum.ORDER_MGMT, UserTypesEnum.INVENTORY_MGMT];

/**
 * Report types the user can filter on, sorted by label: the legacy ReportsReadyFilter list, gated the same
 * way (user type + supplier features). Custom reports collapse into one `custom` option because the API
 * filters on `report_type` only, which is what the legacy select sent for every custom report.
 */
export function getReportTypeOptions(ctx: IReportsFilterContext): IFilterSelectOption[] {
    const { userModel, supplierModel, formatMessage } = ctx;
    const userTypeId = userModel.supplierUserToken?.User_Type?.id;
    const isManagement = userTypeId !== undefined && MANAGEMENT_USER_TYPES.includes(userTypeId);
    const options: { value: ReportTypeEnum; title: string }[] = [];

    if (isManagement) {
        options.push(
            { value: ReportTypeEnum.ORDERS_DETAIL_REPORT, title: "orders_detail_report" },
            { value: ReportTypeEnum.SALES_BY_USER, title: "sales_volume_by_user" },
            { value: ReportTypeEnum.SALES_BY_USER_PRODUCT_DETAIL, title: "sales_volume_by_user_product_detail" },
            { value: ReportTypeEnum.SALES_BY_PRODUCT_BY_CUSTOMER, title: "customer_product_sales" },
            { value: ReportTypeEnum.TOTAL_LOGINS_REPORT, title: "total_logins_report" },
            { value: ReportTypeEnum.ORDERS_CREATED, title: "orders_created_report" },
            { value: ReportTypeEnum.ORDERS_SUBMITTED, title: "orders_submitted_report" },
            { value: ReportTypeEnum.ITEM_IN_ORDERS_REPORT, title: "item_in_orders_report" },
            { value: ReportTypeEnum.OPEN_ORDER_REPORT, title: "open_orders_report" }
        );

        if (supplierModel.has_analytics) {
            options.push({ value: ReportTypeEnum.PRODUCT_SEARCHES_BY_CUSTOMER, title: "product_searches_by_customer" });
        }

        if (supplierModel.has_serials) {
            options.push({ value: ReportTypeEnum.SERIALS_IN_ORDERS_REPORT, title: "serials_in_orders_report" });
        }
    }

    if (supplierModel.has_commission_tiers) {
        options.push({ value: ReportTypeEnum.COMMISSION_TIER_REPORT, title: "commission_tier_report" });
    }

    if (supplierModel.has_commissions) {
        options.push({ value: ReportTypeEnum.COMMISSION_REPORT, title: "commission_report" });
    }

    if (supplierModel.has_rebates) {
        options.push({ value: ReportTypeEnum.VOLUME_REBATE_REPORT, title: "volume_rebate_report" });
    }

    if (supplierModel.has_coop_advertisement) {
        options.push({ value: ReportTypeEnum.COOP_REPORT, title: "coop_advertisement_report" });
    }

    if (supplierModel.has_spiff) {
        options.push({ value: ReportTypeEnum.SPIFF_REPORT, title: "spiff_report" });
    }

    if (supplierModel.has_inventory) {
        options.push({ value: ReportTypeEnum.INVENTORY_STOCK_LEVELS_REPORT, title: "inventory_stock_levels_report" });
        options.push({
            value: ReportTypeEnum.INVENTORY_STOCK_HISTORY_TRANSACTION_REPORT,
            title: "inventory_stock_history_transaction_report",
        });
    }

    if (ctx.hasCustomReports) {
        options.push({ value: ReportTypeEnum.CUSTOM, title: "custom" });
    }

    // Admin only reports
    if (userTypeId === UserTypesEnum.ADMIN) {
        options.push({ value: ReportTypeEnum.PRICE_GAP_REPORT, title: "price_gap_report" });
    }

    options.push({ value: ReportTypeEnum.ACTIVITY_REPORT, title: "activity_report" });
    options.push({ value: ReportTypeEnum.INACTIVE_ACCOUNT_REPORT, title: "inactive_account_report" });
    options.push({ value: ReportTypeEnum.ACCOUNTS_BY_DATE_REPORT, title: "accounts_by_date_report" });

    return options
        .map((option) => ({ label: formatMessage(option.title), value: option.value }))
        .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Compile the Reports filter config. Field keys ARE the `getReports` queryString param names
 * (title, report_type, created_at_range_start, created_at_range_end). The API applies the created range
 * only when both bounds are present, as the legacy filter did.
 */
export function buildReportsFilterSections(ctx: IReportsFilterContext): IFilterSection[] {
    const main: IFilterField[] = [
        { key: "title", label: "title", type: "text", apiKey: "title" },
        {
            key: "report_type",
            label: "report_type",
            type: "select",
            apiKey: "report_type",
            options: [{ label: ctx.formatMessage("all"), value: "" }, ...getReportTypeOptions(ctx)],
        },
        { key: "created_at_range_start", label: "created_from", type: "date", apiKey: "created_at_range_start" },
        { key: "created_at_range_end", label: "created_to", type: "date", apiKey: "created_at_range_end" },
    ];

    return [{ id: "main", title: "search", filters: main }];
}
